import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PricingPlan } from '../types';

const PricingSection: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [showBasicTrainingModal, setShowBasicTrainingModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    participants: '',
    message: ''
  });
  const [basicTrainingFormData, setBasicTrainingFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const pricingPlans: PricingPlan[] = [
    {
      id: 'single',
      title: 'Einzellizenz',
      price: '49,90 €',
      description: 'Pro Nutzer, einmalige Zahlung zzgl. MwSt.',
      features: [
        'Vollständiger Kurszugang',
        'Eine Prüfung inklusive',
        'Personalisiertes Zertifikat',
        'Sofortiger Zugang nach Bezahlung',
        '30 Tage Zugriff auf Inhalte'
      ],
      buttonText: 'Jetzt starten',
      buttonLink: '/registrieren',
      highlighted: true
    },
    {
      id: 'business',
      title: 'Unternehmenspaket',
      price: 'Ab 10 Nutzern',
      description: 'Individualangebot zzgl. MwSt.',
      features: [
        'Vollständiger Kurszugang für alle Mitarbeiter',
        'Prüfungen für alle Teilnehmer',
        'Personalisierte Zertifikate',
        'Übersicht über Fortschritte',
        'Anpassung an Unternehmensrichtlinien möglich',
        'Attraktive Mengenrabatte'
      ],
      buttonText: 'Angebot anfordern',
      buttonLink: '#',
      isEnterprise: true
    },
    {
      id: 'basic-training',
      title: 'Grundschulung KI',
      subtitle: 'mit Praxisfokus',
      price: 'Ideal für den Einstieg',
      description: '',
      features: [
        'Grundlagen KI-Nutzung',
        'Praktische Einführung in aktuelle Systeme',
        'Schwerpunkte: Chatbots, Bild- & Objekterkennung',
        'Mediengenerierung',
        'Sprach- & Audioanwendungen',
        'Schulungsunterlagen',
        'Abschlusstest & Teilnahmezertifikat'
      ],
      buttonText: 'Interesse bekunden',
      buttonLink: '#',
      isComingSoon: true
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.company || !formData.email || !formData.participants) {
      setSubmitStatus({
        type: 'error',
        message: 'Bitte füllen Sie alle Pflichtfelder aus.'
      });
      return;
    }

    if (parseInt(formData.participants) < 10) {
      setSubmitStatus({
        type: 'error',
        message: 'Unternehmenspaket ist ab 10 Teilnehmern verfügbar.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Prepare email content
      const emailSubject = `Angebotsanfrage Unternehmenspaket - ${formData.company}`;
      const emailContent = `
        <h2 style="color: #2574B5; margin-bottom: 20px;">Neue Angebotsanfrage für Unternehmenspaket</h2>
        
        <div style="background-color: #f9f9f9; padding: 15px; margin-bottom: 20px; border-radius: 5px;">
          <h3 style="color: #2574B5; margin: 0 0 10px 0;">Kontaktdaten</h3>
          <p style="margin: 5px 0;"><strong>Name:</strong> ${formData.name}</p>
          <p style="margin: 5px 0;"><strong>Unternehmen:</strong> ${formData.company}</p>
          <p style="margin: 5px 0;"><strong>E-Mail:</strong> ${formData.email}</p>
        </div>
        
        <div style="background-color: #f0f8ff; padding: 15px; margin-bottom: 20px; border-radius: 5px;">
          <h3 style="color: #2574B5; margin: 0 0 10px 0;">Anfrage-Details</h3>
          <p style="margin: 5px 0;"><strong>Anzahl Teilnehmer:</strong> ${formData.participants}</p>
          ${formData.message ? `<p style="margin: 5px 0;"><strong>Nachricht:</strong></p><p style="margin: 5px 0; padding: 10px; background-color: white; border-radius: 3px;">${formData.message.replace(/\n/g, '<br>')}</p>` : ''}
        </div>
        
        <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border: 1px solid #ffeaa7;">
          <h3 style="color: #856404; margin: 0 0 10px 0;">Empfohlene nächste Schritte</h3>
          <ol style="color: #856404; margin: 0; padding-left: 20px;">
            <li>Kontaktaufnahme innerhalb von 24 Stunden</li>
            <li>Bedarfsanalyse und individuelle Beratung</li>
            <li>Erstellung eines maßgeschneiderten Angebots</li>
            <li>Präsentation der Lösung und Preisgestaltung</li>
          </ol>
        </div>
        
        <p style="margin-top: 20px; color: #666; font-size: 14px;">
          Diese Anfrage wurde über das Kontaktformular auf der KI-Navigatoren Website gesendet.
        </p>
      `;

      // Send email via send-mail Edge Function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          to: 'aiko@ki-navigatoren.de',
          subject: emailSubject,
          body: emailContent,
          headerTitle: 'Neue Angebotsanfrage',
          headerSubtitle: 'Unternehmenspaket KI-Kompetenznachweis'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Senden der Anfrage');
      }

      // Send confirmation email to the requester
      const confirmationSubject = 'Bestätigung Ihrer Angebotsanfrage - KI-Navigatoren';
      const confirmationContent = `
        <p style="font-size: 16px; color: #333; margin-bottom: 15px;">
          Willkommen beim Training für den EU AI Act Kompetenznachweis, ${formData.name}!
        </p>
        
        <p style="font-size: 16px; color: #333; line-height: 1.5; margin-bottom: 20px;">
          vielen Dank für Ihr Interesse an unserem KI-Kompetenznachweis Unternehmenspaket! 
          Wir haben Ihre Anfrage erhalten und werden uns innerhalb von 24 Stunden bei Ihnen melden.
        </p>
        
        <div style="background-color: #f9f9f9; padding: 15px; margin-bottom: 20px; border-radius: 5px; border: 1px solid #e0e0e0;">
          <h3 style="color: #2574B5; margin: 0 0 10px 0; font-size: 16px;">Ihre Anfrage im Überblick:</h3>
          <p style="margin: 5px 0;"><strong>Unternehmen:</strong> ${formData.company}</p>
          <p style="margin: 5px 0;"><strong>Anzahl Teilnehmer:</strong> ${formData.participants}</p>
          <p style="margin: 5px 0;"><strong>Kontakt-E-Mail:</strong> ${formData.email}</p>
          ${formData.message ? `<p style="margin: 5px 0;"><strong>Ihre Nachricht:</strong></p><p style="margin: 5px 0; padding: 10px; background-color: white; border-radius: 3px; font-style: italic;">${formData.message.replace(/\n/g, '<br>')}</p>` : ''}
        </div>
        
        <div style="background-color: #f0f8ff; padding: 15px; margin-bottom: 20px; border-radius: 5px; border: 1px solid #b3d9ff;">
          <h3 style="color: #2574B5; margin: 0 0 10px 0; font-size: 16px;">Was passiert als nächstes?</h3>
          <ol style="color: #333; margin: 0; padding-left: 20px; line-height: 1.6;">
            <li>Unser Team prüft Ihre Anfrage und bereitet ein individuelles Angebot vor</li>
            <li>Wir kontaktieren Sie innerhalb von 24 Stunden für ein persönliches Gespräch</li>
            <li>Gemeinsam besprechen wir Ihre spezifischen Anforderungen</li>
            <li>Sie erhalten ein maßgeschneidertes Angebot für Ihr Unternehmen</li>
          </ol>
        </div>
        
        <div style="background-color: #fff3cd; border: 1px solid #ffeaa7; padding: 15px; margin-bottom: 20px; border-radius: 5px;">
          <h3 style="color: #856404; margin: 0 0 10px 0; font-size: 16px;">Warum KI-Navigatoren?</h3>
          <ul style="color: #856404; margin: 0; padding-left: 20px; line-height: 1.6;">
            <li>Rechtssicherheit nach EU AI Act</li>
            <li>Praxiserprobte Schulungsinhalte</li>
            <li>Flexible Unternehmensanpassungen</li>
            <li>Persönliche Betreuung durch KI-Experten</li>
            <li>Attraktive Mengenrabatte ab 10 Teilnehmern</li>
          </ul>
        </div>
        
        <p style="font-size: 16px; color: #333; line-height: 1.5; margin-bottom: 20px;">
          Bei Rückfragen erreichen Sie uns jederzeit unter 
          <a href="mailto:aiko@ki-navigatoren.de" style="color: #2574B5;">aiko@ki-navigatoren.de</a> 
          oder telefonisch unter +49 (0) 7363 8189409.
        </p>
        
        <p style="font-size: 14px; color: #333; margin-bottom: 0;">
          Mit freundlichen Grüßen<br>
          KI-Navigatoren GmbH
        </p>
      `;

      const confirmationResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          to: formData.email,
          subject: confirmationSubject,
          body: confirmationContent,
          headerTitle: 'Bestätigung Ihrer Anfrage',
          headerSubtitle: 'KI-Kompetenznachweis Unternehmenspaket'
        }),
      });

      if (!confirmationResponse.ok) {
        console.warn('Failed to send confirmation email to requester');
        // Don't throw error here as the main request was successful
      }
      setSubmitStatus({
        type: 'success',
        message: 'Ihre Anfrage wurde erfolgreich gesendet! Sie erhalten eine Bestätigungsmail und wir melden uns innerhalb von 24 Stunden bei Ihnen.'
      });

      // Reset form
      setFormData({
        name: '',
        company: '',
        email: '',
        participants: '',
        message: ''
      });

      // Close modal after 3 seconds
      setTimeout(() => {
        setShowModal(false);
        setSubmitStatus({ type: null, message: '' });
      }, 3000);

    } catch (error) {
      console.error('Error sending quote request:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Fehler beim Senden der Anfrage. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setFormData({
      name: '',
      company: '',
      email: '',
      participants: '',
      message: ''
    });
    setSubmitStatus({ type: null, message: '' });
    setIsSubmitting(false);
  };

  return (
    <>
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Transparente Preisgestaltung</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wählen Sie die Option, die am besten zu Ihren Anforderungen passt.
              Einzellizenzen sofort verfügbar, Unternehmensangebote individuell anpassbar.
              Alle Preise verstehen sich zzgl. der gesetzlichen Mehrwertsteuer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-lg overflow-hidden transition-all duration-300 flex flex-col ${
                  plan.highlighted
                    ? 'bg-white border-2 border-[#EE7F01] shadow-xl transform hover:-translate-y-2'
                    : plan.isComingSoon
                    ? 'bg-gradient-to-br from-gray-50 to-blue-50 border-2 border-[#2574B5] border-dashed shadow-md hover:-translate-y-1'
                    : 'bg-white border-2 border-[#2574B5] shadow-md hover:-translate-y-1'
                }`}
              >
                {plan.highlighted && (
                  <div className="bg-[#EE7F01] text-white text-center py-2 font-medium">
                    Beliebteste Option
                  </div>
                )}
                {plan.isEnterprise && (
                  <div className="bg-[#2574B5] text-white text-center py-2 font-medium">
                    Enterprise Buchung
                  </div>
                )}
                {plan.isComingSoon && (
                  <div className="bg-gradient-to-r from-[#2574B5] to-[#EE7F01] text-white text-center py-2 font-medium flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Bald verfügbar
                  </div>
                )}
                <div className="p-8 flex-grow">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {plan.title}
                    {plan.subtitle && <span className="block text-xl text-gray-600 font-normal mt-1">{plan.subtitle}</span>}
                  </h3>
                  <div className="mb-6">
                    <span className={`text-3xl font-bold ${
                      plan.isComingSoon ? 'text-[#2574B5]' : 'text-[#2574B5]'
                    }`}>{plan.price}</span>
                    {plan.description && <span className="text-gray-600 ml-2">{plan.description}</span>}
                  </div>
                  
                  <ul className="mb-8 space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="p-8 pt-0">
                  {plan.id === 'single' ? (
                    <Link
                      to={plan.buttonLink}
                      className="block w-full text-center bg-[#EE7F01] hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-md transition-colors"
                    >
                      {plan.buttonText}
                    </Link>
                  ) : plan.isComingSoon ? (
                    <button
                      onClick={() => setShowBasicTrainingModal(true)}
                      className="block w-full text-center bg-gradient-to-r from-[#2574B5] to-[#EE7F01] hover:from-[#1d5a8f] hover:to-[#d97001] text-white font-medium py-3 px-4 rounded-md transition-all"
                    >
                      {plan.buttonText}
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowModal(true)}
                      className="block w-full text-center bg-[#2574B5] hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition-colors"
                    >
                      {plan.buttonText}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Contact Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Angebot anfordern</h3>
            
            {submitStatus.type && (
              <div className={`mb-4 p-4 rounded-lg ${
                submitStatus.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {submitStatus.message}
              </div>
            )}
            
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Vor- und Nachname"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Unternehmen</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Firma"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="ihre-email@beispiel.de"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div>
                <label htmlFor="participants" className="block text-sm font-medium text-gray-700 mb-1">Anzahl der Teilnehmer</label>
                <input
                  type="number"
                  id="participants"
                  name="participants"
                  value={formData.participants}
                  onChange={handleInputChange}
                  placeholder="z.B. 15"
                  min="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Nachricht (optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Ihre Nachricht oder spezifische Anforderungen"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  disabled={isSubmitting}
                ></textarea>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || submitStatus.type === 'success'}
                  className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-[#2574B5] hover:bg-blue-700'
                  }`}
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Angebot anfordern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Basic Training Interest Modal */}
      {showBasicTrainingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowBasicTrainingModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <h3 className="text-2xl font-bold text-gray-800 mb-2">Interesse an Grundschulung KI</h3>
            <p className="text-gray-600 mb-4">Lassen Sie uns wissen, dass Sie interessiert sind. Wir melden uns bei Ihnen, um die verfügbaren Optionen durchzusprechen.</p>

            {submitStatus.type && (
              <div className={`mb-4 p-4 rounded-lg ${
                submitStatus.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {submitStatus.message}
              </div>
            )}

            <form className="space-y-4" onSubmit={async (e) => {
              e.preventDefault();

              if (!basicTrainingFormData.name || !basicTrainingFormData.email) {
                setSubmitStatus({
                  type: 'error',
                  message: 'Bitte füllen Sie alle Pflichtfelder aus.'
                });
                return;
              }

              setIsSubmitting(true);
              setSubmitStatus({ type: null, message: '' });

              try {
                const emailSubject = `Interessensbekundung Grundschulung KI - ${basicTrainingFormData.name}`;
                const emailContent = `
                  <h2 style="color: #2574B5; margin-bottom: 20px;">Neue Interessensbekundung für Grundschulung KI</h2>

                  <div style="background-color: #f9f9f9; padding: 15px; margin-bottom: 20px; border-radius: 5px;">
                    <h3 style="color: #2574B5; margin: 0 0 10px 0;">Kontaktdaten</h3>
                    <p style="margin: 5px 0;"><strong>Name:</strong> ${basicTrainingFormData.name}</p>
                    ${basicTrainingFormData.company ? `<p style="margin: 5px 0;"><strong>Unternehmen:</strong> ${basicTrainingFormData.company}</p>` : ''}
                    <p style="margin: 5px 0;"><strong>E-Mail:</strong> ${basicTrainingFormData.email}</p>
                    ${basicTrainingFormData.phone ? `<p style="margin: 5px 0;"><strong>Telefon:</strong> ${basicTrainingFormData.phone}</p>` : ''}
                  </div>

                  ${basicTrainingFormData.message ? `
                    <div style="background-color: #f0f8ff; padding: 15px; margin-bottom: 20px; border-radius: 5px;">
                      <h3 style="color: #2574B5; margin: 0 0 10px 0;">Nachricht</h3>
                      <p style="margin: 5px 0; padding: 10px; background-color: white; border-radius: 3px;">${basicTrainingFormData.message.replace(/\n/g, '<br>')}</p>
                    </div>
                  ` : ''}

                  <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border: 1px solid #ffeaa7;">
                    <h3 style="color: #856404; margin: 0 0 10px 0;">Empfohlene nächste Schritte</h3>
                    <ol style="color: #856404; margin: 0; padding-left: 20px;">
                      <li>Kontakt aufnehmen und über Details informieren</li>
                      <li>Bei Launch der Grundschulung benachrichtigen</li>
                      <li>Optional: Individuelles Angebot erstellen</li>
                    </ol>
                  </div>

                  <p style="margin-top: 20px; color: #666; font-size: 14px;">
                    Diese Interessensbekundung wurde über das Formular auf der KI-Navigatoren Website gesendet.
                  </p>
                `;

                const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-mail`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  },
                  body: JSON.stringify({
                    to: 'aiko@ki-navigatoren.de',
                    subject: emailSubject,
                    body: emailContent,
                    headerTitle: 'Neue Interessensbekundung',
                    headerSubtitle: 'Grundschulung KI mit Praxisfokus'
                  }),
                });

                if (!response.ok) {
                  const errorData = await response.json();
                  throw new Error(errorData.error || 'Fehler beim Senden der Anfrage');
                }

                const confirmationSubject = 'Vielen Dank für Ihr Interesse - Grundschulung KI';
                const confirmationContent = `
                  <p style="font-size: 16px; color: #333; margin-bottom: 15px;">
                    Hallo ${basicTrainingFormData.name},
                  </p>

                  <p style="font-size: 16px; color: #333; line-height: 1.5; margin-bottom: 20px;">
                    vielen Dank für Ihr Interesse an unserer Grundschulung KI mit Praxisfokus!
                    Wir haben Ihre Interessensbekundung erhalten und freuen uns über Ihr Interesse.
                  </p>

                  <div style="background-color: #f0f8ff; padding: 15px; margin-bottom: 20px; border-radius: 5px; border: 1px solid #b3d9ff;">
                    <h3 style="color: #2574B5; margin: 0 0 10px 0; font-size: 16px;">Die Grundschulung im Überblick:</h3>
                    <ul style="color: #333; margin: 0; padding-left: 20px; line-height: 1.6;">
                      <li>Grundlagen KI-Nutzung</li>
                      <li>Praktische Einführung in aktuelle Systeme</li>
                      <li>Schwerpunkte: Chatbots, Bild- & Objekterkennung</li>
                      <li>Mediengenerierung</li>
                      <li>Sprach- & Audioanwendungen</li>
                      <li>Schulungsunterlagen inklusive</li>
                      <li>Abschlusstest & Teilnahmezertifikat</li>
                    </ul>
                  </div>

                  <div style="background-color: #f9f9f9; padding: 15px; margin-bottom: 20px; border-radius: 5px; border: 1px solid #e0e0e0;">
                    <h3 style="color: #2574B5; margin: 0 0 10px 0; font-size: 16px;">Wie geht es weiter?</h3>
                    <p style="color: #333; margin: 0; line-height: 1.6;">
                      Wir informieren Sie umgehend, sobald die Grundschulung verfügbar ist.
                      Falls Sie spezifische Fragen haben oder ein individuelles Angebot wünschen,
                      melden wir uns gerne persönlich bei Ihnen.
                    </p>
                  </div>

                  <p style="font-size: 16px; color: #333; line-height: 1.5; margin-bottom: 20px;">
                    Bei Rückfragen erreichen Sie uns jederzeit unter
                    <a href="mailto:aiko@ki-navigatoren.de" style="color: #2574B5;">aiko@ki-navigatoren.de</a>
                    oder telefonisch unter +49 (0) 7363 8189409.
                  </p>

                  <p style="font-size: 14px; color: #333; margin-bottom: 0;">
                    Mit freundlichen Grüßen<br>
                    Ihr KI-Navigatoren Team
                  </p>
                `;

                await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-mail`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  },
                  body: JSON.stringify({
                    to: basicTrainingFormData.email,
                    subject: confirmationSubject,
                    body: confirmationContent,
                    headerTitle: 'Vielen Dank für Ihr Interesse',
                    headerSubtitle: 'Grundschulung KI mit Praxisfokus'
                  }),
                });

                setSubmitStatus({
                  type: 'success',
                  message: 'Vielen Dank! Wir haben Ihre Interessensbekundung erhalten und melden uns bei Ihnen, sobald die Grundschulung verfügbar ist.'
                });

                setBasicTrainingFormData({
                  name: '',
                  company: '',
                  email: '',
                  phone: '',
                  message: ''
                });

                setTimeout(() => {
                  setShowBasicTrainingModal(false);
                  setSubmitStatus({ type: null, message: '' });
                }, 3000);

              } catch (error) {
                console.error('Error sending interest request:', error);
                setSubmitStatus({
                  type: 'error',
                  message: 'Fehler beim Senden der Anfrage. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.'
                });
              } finally {
                setIsSubmitting(false);
              }
            }}>
              <div>
                <label htmlFor="bt-name" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  id="bt-name"
                  name="name"
                  value={basicTrainingFormData.name}
                  onChange={(e) => setBasicTrainingFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Vor- und Nachname"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="bt-company" className="block text-sm font-medium text-gray-700 mb-1">Unternehmen</label>
                <input
                  type="text"
                  id="bt-company"
                  name="company"
                  value={basicTrainingFormData.company}
                  onChange={(e) => setBasicTrainingFormData(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="Firma (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="bt-email" className="block text-sm font-medium text-gray-700 mb-1">E-Mail *</label>
                <input
                  type="email"
                  id="bt-email"
                  name="email"
                  value={basicTrainingFormData.email}
                  onChange={(e) => setBasicTrainingFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="ihre-email@beispiel.de"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="bt-phone" className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <input
                  type="tel"
                  id="bt-phone"
                  name="phone"
                  value={basicTrainingFormData.phone}
                  onChange={(e) => setBasicTrainingFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+49 123 456789 (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="bt-message" className="block text-sm font-medium text-gray-700 mb-1">Nachricht</label>
                <textarea
                  id="bt-message"
                  name="message"
                  value={basicTrainingFormData.message}
                  onChange={(e) => setBasicTrainingFormData(prev => ({ ...prev, message: e.target.value }))}
                  rows={3}
                  placeholder="Spezifische Anforderungen oder Fragen (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2574B5]"
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowBasicTrainingModal(false);
                    setBasicTrainingFormData({
                      name: '',
                      company: '',
                      email: '',
                      phone: '',
                      message: ''
                    });
                    setSubmitStatus({ type: null, message: '' });
                    setIsSubmitting(false);
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || submitStatus.type === 'success'}
                  className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 ${
                    isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#2574B5] hover:bg-[#1d5a8f]'
                  }`}
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Unverbindlich anfragen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default PricingSection;